import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Link2, Loader2, Plus, Trash2, Users } from 'lucide-react';
import {
  AdminAssessmentInvite,
  AdminAssessmentWorkshop,
  assessmentInvitesService,
} from '../../services/api';
import { inviteLink, smsLink } from './links';

const fa = (n: number) => n.toLocaleString('fa-IR');

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';


interface Props {
  assessmentId: string;
  assessmentSlug: string;
  inviteOnly: boolean;
  workshops: AdminAssessmentWorkshop[];
  onToggleAccessMode: (mode: 'PUBLIC' | 'INVITE_ONLY') => void;
}

export const AssessmentInvites: React.FC<Props> = ({
  assessmentId,
  assessmentSlug,
  inviteOnly,
  workshops,
  onToggleAccessMode,
}) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invites, setInvites] = useState<AdminAssessmentInvite[]>([]);
  const [copiedId, setCopiedId] = useState('');

  const [form, setForm] = useState({ workshopId: '', label: '', phones: '', count: '1', maxUses: '1' });
  const [filter, setFilter] = useState<'all' | 'unused' | 'used' | 'completed'>('all');

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 3000);
  };
  const fail = (err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvites(await assessmentInvitesService.list(assessmentId));
    } catch (err: any) {
      fail(err, 'دریافت دعوت‌نامه‌ها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const workshopSlugById = useMemo(
    () => new Map(workshops.map((w) => [w.id, w.slug])),
    [workshops],
  );

  const stats = useMemo(() => {
    const used = invites.filter((i) => i.usedCount > 0).length;
    const completed = invites.filter((i) => i.claims.some((c) => c.completed)).length;
    return { total: invites.length, used, completed, unused: invites.length - used };
  }, [invites]);

  const visible = useMemo(() => {
    if (filter === 'unused') return invites.filter((i) => i.usedCount === 0);
    if (filter === 'used') return invites.filter((i) => i.usedCount > 0);
    if (filter === 'completed') return invites.filter((i) => i.claims.some((c) => c.completed));
    return invites;
  }, [invites, filter]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = () =>
    run(async () => {
      const phones = form.phones
        .split(/[\s,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);

      const payload = {
        workshopId: form.workshopId || undefined,
        label: form.label.trim() || undefined,
        maxUses: Number(form.maxUses) || 1,
        ...(phones.length ? { phones } : { count: Number(form.count) || 1 }),
      };

      const result = await assessmentInvitesService.bulk(assessmentId, payload);
      await load();
      setForm((prev) => ({ ...prev, phones: '' }));
      flash(`${fa(result.created)} دعوت‌نامه ساخته شد.`);
    }, 'ساخت دعوت‌نامه انجام نشد.');

  const handleFromWorkshop = () =>
    run(async () => {
      if (!form.workshopId) {
        setError('اول یک کارگاه انتخاب کنید.');
        return;
      }
      const result = await assessmentInvitesService.fromWorkshop(assessmentId, form.workshopId);
      await load();
      flash(`${fa(result.created)} دعوت‌نامه برای شرکت‌کنندگان کارگاه ساخته شد.`);
    }, 'ساخت دعوت از روی کارگاه انجام نشد.');

  const copy = async (invite: AdminAssessmentInvite) => {
    const link = inviteLink(assessmentSlug, invite.code, workshopSlugById.get(invite.workshop?.id || ''));
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(''), 1800);
    } catch {
      window.prompt('لینک را کپی کنید:', link);
    }
  };

  const copyAllUnused = async () => {
    const links = invites
      .filter((i) => i.usedCount === 0 && i.isActive)
      .map((i) => {
        /* خروجی مستقیم برای پنل پیامک؛ پس نسخهٔ کوتاه بدون //:https */
        const link = smsLink(inviteLink(assessmentSlug, i.code, workshopSlugById.get(i.workshop?.id || '')));
        return i.phone ? `${i.phone}\t${link}` : link;
      })
      .join('\n');
    if (!links) {
      setError('دعوت استفاده‌نشده‌ای وجود ندارد.');
      return;
    }
    try {
      await navigator.clipboard.writeText(links);
      flash('لینک‌های استفاده‌نشده کپی شد (هر خط: شماره و لینک).');
    } catch {
      window.prompt('کپی کنید:', links);
    }
  };

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          {notice}
        </div>
      ) : null}

      {/* کلید اصلی: بدون این، لینک‌ها ساخته می‌شوند ولی هرکسی هم می‌تواند وارد شود */}
      <div
        className={`rounded-xl border p-4 ${
          inviteOnly ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-gray-900">
              {inviteOnly ? 'فقط با دعوت‌نامه' : 'باز برای همه'}
            </p>
            <p className="mt-0.5 text-[11.5px] font-bold text-gray-600">
              {inviteOnly
                ? 'فقط کسانی که لینک اختصاصی گرفته‌اند می‌توانند شرکت کنند. دکمهٔ شروع در داشبورد برای بقیه نمایش داده نمی‌شود.'
                : 'در این حالت هر کاربر واردشده‌ای می‌تواند از داشبورد پرسشنامه را شروع کند.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onToggleAccessMode(inviteOnly ? 'PUBLIC' : 'INVITE_ONLY')}
            className={`rounded-lg px-4 py-2 text-sm font-bold text-white ${
              inviteOnly ? 'bg-gray-600' : 'bg-green-600'
            }`}
          >
            {inviteOnly ? 'باز کردن برای همه' : 'محدود کردن به دعوت‌نامه'}
          </button>
        </div>
      </div>

      {/* ساخت دعوت */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-3 text-sm font-bold text-gray-800">ساخت دعوت‌نامه</p>
        <div className="grid gap-3 md:grid-cols-4">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">کارگاه</span>
            <select
              value={form.workshopId}
              onChange={(e) => setForm({ ...form, workshopId: e.target.value })}
              className={inputClass}
            >
              <option value="">— بدون کارگاه —</option>
              {workshops.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.title}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">برچسب (یادداشت)</span>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="مثلاً: دورهٔ مرداد"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">تعداد (بدون شماره)</span>
            <input
              type="number"
              min={1}
              value={form.count}
              onChange={(e) => setForm({ ...form, count: e.target.value })}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">سقف استفادهٔ هر لینک</span>
            <input
              type="number"
              min={1}
              value={form.maxUses}
              onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-bold text-gray-600">
            یا شماره موبایل‌ها — هر شماره یک لینک شخصی می‌گیرد که فقط با همان شماره باز می‌شود
          </span>
          <textarea
            value={form.phones}
            onChange={(e) => setForm({ ...form, phones: e.target.value })}
            rows={3}
            placeholder="09121234567, 09351112233 — یا هر خط یک شماره"
            className={inputClass}
          />
        </label>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={handleGenerate}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            ساخت دعوت‌نامه
          </button>
          <button
            type="button"
            disabled={busy || !form.workshopId}
            onClick={handleFromWorkshop}
            title="برای همهٔ شرکت‌کنندگان کارگاه واقعیِ متصل، یک لینک شخصی می‌سازد"
            className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700 disabled:opacity-50"
          >
            <Users className="h-4 w-4" />
            از روی شرکت‌کنندگان کارگاه
          </button>
          <button
            type="button"
            onClick={copyAllUnused}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700"
          >
            <Copy className="h-4 w-4" />
            کپی همهٔ لینک‌های استفاده‌نشده
          </button>
        </div>
      </div>

      {/* لیست */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-4">
          {(
            [
              ['all', `همه (${fa(stats.total)})`],
              ['unused', `استفاده‌نشده (${fa(stats.unused)})`],
              ['used', `باز شده (${fa(stats.used)})`],
              ['completed', `تکمیل‌شده (${fa(stats.completed)})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                filter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm font-bold text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            در حال بارگذاری…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-right text-sm">
              <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
                <tr>
                  <th className="px-4 py-3">کد</th>
                  <th className="px-4 py-3">برای</th>
                  <th className="px-4 py-3">کارگاه</th>
                  <th className="px-4 py-3">وضعیت</th>
                  <th className="px-4 py-3">استفاده</th>
                  <th className="px-4 py-3">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((invite) => {
                  const claim = invite.claims[0];
                  return (
                    <tr key={invite.id} className={invite.isActive ? '' : 'opacity-50'}>
                      <td className="px-4 py-3">
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold">
                          {invite.code}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {invite.phone ? (
                          <span className="font-bold text-gray-800">{invite.phone}</span>
                        ) : (
                          <span className="text-gray-400">عمومی</span>
                        )}
                        {invite.label ? (
                          <span className="block text-[10.5px] text-gray-400">{invite.label}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {invite.workshop?.title || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {claim?.completed ? (
                          <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10.5px] font-bold text-green-700">
                            آزمون را تمام کرده
                          </span>
                        ) : claim ? (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">
                            باز کرده، ناتمام
                          </span>
                        ) : (
                          <span className="rounded-full border border-gray-200 px-2 py-0.5 text-[10.5px] font-bold text-gray-400">
                            هنوز باز نشده
                          </span>
                        )}
                        {claim?.name ? (
                          <span className="mt-0.5 block text-[10.5px] text-gray-500">
                            {claim.name}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs font-bold tabular-nums text-gray-600">
                        {fa(invite.usedCount)} / {fa(invite.maxUses)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => copy(invite)}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-bold text-gray-700"
                          >
                            {copiedId === invite.id ? (
                              <>
                                <Check className="h-3 w-3 text-green-600" />
                                کپی شد
                              </>
                            ) : (
                              <>
                                <Link2 className="h-3 w-3" />
                                کپی لینک
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              run(async () => {
                                await assessmentInvitesService.update(invite.id, {
                                  isActive: !invite.isActive,
                                });
                                await load();
                              }, 'تغییر وضعیت انجام نشد.')
                            }
                            className="rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-bold text-gray-600"
                          >
                            {invite.isActive ? 'غیرفعال' : 'فعال'}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              run(async () => {
                                if (!window.confirm('این دعوت‌نامه حذف شود؟')) return;
                                await assessmentInvitesService.remove(invite.id);
                                await load();
                              }, 'حذف انجام نشد.')
                            }
                            className="rounded-lg p-1 text-red-600 hover:bg-red-50"
                            aria-label="حذف"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!visible.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center font-bold text-gray-400">
                      دعوت‌نامه‌ای در این دسته نیست.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
